<script setup>
import { computed, getCurrentInstance, ref, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Panel from 'primevue/panel';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { getStatusLabel, getStatusSeverity } from '@/composables/useOrderStatus.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const copiedLinkId = ref(null);

// Currency conversion
const { formatWithSymbol, getCurrentCurrency, onCurrencyChange, exchangeRates, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const props = defineProps({
    cartsWithSite: {
        type: Array,
        default: () => []
    },
    ordersWithSite: {
        type: Array,
        default: () => []
    },
    liveLinksWithSite: {
        type: Array,
        default: () => []
    },
    offerHistory: {
        type: Array,
        default: () => []
    }
});

// Filter states for Offers History
const selectedNiche = ref(null);
const showOnlyDisplayPriceChanges = ref(false);
const selectedChangeType = ref(null);

// Constants for change type filter
const FORMULA_CHANGE_FILTER = 'Formula change';
const PRICE_CHANGE_FILTER = 'Price change';
const DELETED_FILTER = 'Deleted';

// Niche options (unique type names from history)
const nicheOptions = computed(() => {
    const niches = props.offerHistory
        .map(item => item.type_name)
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort();

    return [
        { label: $t('All niches'), value: null },
        ...niches.map(niche => ({ label: niche, value: niche }))
    ];
});

// Change type options
const changeTypeOptions = computed(() => {
    return [
        { label: $t('All changes'), value: null },
        { label: $t('Formula changes'), value: FORMULA_CHANGE_FILTER },
        { label: $t('Price changes'), value: PRICE_CHANGE_FILTER },
        { label: $t('Deletions'), value: DELETED_FILTER },
    ];
});

const isReferencePriceItem = (item) => {
    const oldDisplayPrice = item.old_values?.display_price === true;
    const newDisplayPrice = item.new_values?.display_price === true;

    return oldDisplayPrice || newDisplayPrice;
};

const isFormulaChangeItem = (item) => {
    return item.new_values?.formula_changed === true;
};

const isPriceChangeItem = (item) => {
    // Check if cost or sale price changed (not a formula change)
    if (isFormulaChangeItem(item)) return false;

    const oldCost = item.old_values?.cost;
    const newCost = item.new_values?.cost;
    const oldSale = item.old_values?.sale;
    const newSale = item.new_values?.sale;

    return (oldCost !== undefined && oldCost !== newCost) ||
           (oldSale !== undefined && oldSale !== newSale);
};

const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    // percentage_addition is stored as basis points (e.g., 1000 = 10%)
    return `${(value / 100).toFixed(1)}%`;
};

const formatFixedAddition = (value, currency) => {
    if (value === null || value === undefined) return '$0';
    // fixed_addition is stored in centavos
    const amount = value / 100;
    const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
    return `${symbol}${amount.toFixed(0)}`;
};

const getFormulaTransition = (item) => {
    if (!isFormulaChangeItem(item)) return null;

    const oldPercentage = item.old_values?.percentage_addition;
    const newPercentage = item.new_values?.percentage_addition;
    const oldFixed = item.old_values?.fixed_addition;
    const newFixed = item.new_values?.fixed_addition;
    const currency = item.cost_coin || 'USD';

    const parts = [];

    // Show percentage change if different
    if (oldPercentage !== newPercentage) {
        parts.push(`${formatPercentage(oldPercentage)} → ${formatPercentage(newPercentage)}`);
    }

    // Show fixed addition change if different
    if (oldFixed !== newFixed) {
        parts.push(`${formatFixedAddition(oldFixed, currency)} → ${formatFixedAddition(newFixed, currency)}`);
    }

    return parts.length > 0 ? parts.join(', ') : null;
};

const filteredOfferHistory = computed(() => {
    let result = props.offerHistory;

    // Filter by niche
    if (selectedNiche.value) {
        result = result.filter(item => item.type_name === selectedNiche.value);
    }

    // Filter by change type
    if (selectedChangeType.value === FORMULA_CHANGE_FILTER) {
        result = result.filter(item => isFormulaChangeItem(item));
    } else if (selectedChangeType.value === PRICE_CHANGE_FILTER) {
        result = result.filter(item => isPriceChangeItem(item));
    } else if (selectedChangeType.value === DELETED_FILTER) {
        result = result.filter(item => item.action === 'deleted');
    }

    // Filter to show only changes that affected the displayed price
    if (showOnlyDisplayPriceChanges.value) {
        result = result.filter(item => isReferencePriceItem(item));
    }

    return result;
});

const changesSelectTypeLabel = computed(() => {
    const parts = [];

    if (selectedNiche.value) {
        parts.push(selectedNiche.value);
    }

    if (selectedChangeType.value) {
        parts.push(selectedChangeType.value);
    }

    if (showOnlyDisplayPriceChanges.value) {
        parts.push($t('Display Price'));
    }

    if (parts.length === 0) {
        return $t('Total Changes');
    }

    return parts.join(' - ') + ' ' + $t('Changes');
});

const getOrderStatusLabel = (status) => getStatusLabel(status, $t);
const getOrderStatusSeverity = (status) => getStatusSeverity(status);

const sumSaleInCurrentCurrency = (orders) =>
    orders.reduce((sum, o) => sum + convertPrice(o.sale || 0, o.sale_coin || 'BRL', currentCurrency.value), 0);

const totalCompletedSale = computed(() =>
    sumSaleInCurrentCurrency(props.ordersWithSite.filter(o => o.status === 'COMPLETED' || o.status === 'INVOICE'))
);

const totalPendingSale = computed(() =>
    sumSaleInCurrentCurrency(props.ordersWithSite.filter(o => o.status !== 'CANCELED'))
);

const getActionSeverity = (action) => {
    switch (action) {
        case 'created': return 'success';
        case 'updated': return 'warn';
        case 'deleted': return 'danger';
        default: return 'info';
    }
};

const getActionLabel = (action) => {
    switch (action) {
        case 'created': return $t('Created');
        case 'updated': return $t('Updated');
        case 'deleted': return $t('Deleted');
        default: return action;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
};

const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
};

const formatPrice = (price, currency) => {
    if (!price || !currency) return '-';

    const rates = exchangeRates.value;

    // If no exchange rates available, show in original currency
    if (!rates || Object.keys(rates).length === 0) {
        const value = price / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }

    // Convert to user's selected currency
    const convertedValue = convertPrice(price, currency, currentCurrency.value);
    return formatWithSymbol(convertedValue, currentCurrency.value);
};

// Listen for currency changes
onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});

const copyToClipboard = async (text, itemId) => {
    try {
        await navigator.clipboard.writeText(text);
        copiedLinkId.value = itemId;
        toast.add({
            severity: 'success',
            summary: $t('Copied!'),
            detail: $t('Link copied to clipboard'),
            life: 3000
        });
        setTimeout(() => {
            copiedLinkId.value = null;
        }, 2000);
    } catch (err) {
        console.error('Failed to copy:', err);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to copy link'),
            life: 3000
        });
    }
};
</script>

<template>
    <div class="flex flex-col gap-4">
        <!-- Carts Section -->
        <Panel toggleable collapsed>
            <template #header>
                <div class="flex items-center gap-3">
                    <i class="pi pi-shopping-cart text-blue-600"></i>
                    <span class="font-semibold">{{ $t('In Client Carts') }}</span>
                    <Tag severity="secondary" rounded class="ml-2">{{ cartsWithSite.length }}</Tag>
                </div>
            </template>
            <DataTable
                :value="cartsWithSite"
                paginator
                :rows="5"
                dataKey="id"
                class="p-datatable-sm"
                stripedRows
                :rowHover="true"
            >
                <template #empty>
                    <div class="w-full flex flex-col items-center justify-center py-6 text-surface-500">
                        <i class="pi pi-shopping-cart text-4xl mb-2 text-surface-300"></i>
                        <span class="text-sm">{{ $t('This site is not in any client cart') }}</span>
                    </div>
                </template>

                <Column field="client_name" :header="$t('Client')" sortable style="min-width: 200px">
                    <template #body="slotProps">
                        <div class="flex items-center gap-2">
                            <i class="pi pi-user text-blue-600 text-sm"></i>
                            <span class="font-medium text-surface-800">{{ slotProps.data.client_name }}</span>
                        </div>
                    </template>
                </Column>
                <Column field="client_email" :header="$t('Email')" sortable style="min-width: 200px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-600">{{ slotProps.data.client_email }}</span>
                    </template>
                </Column>
                <Column field="quantity" :header="$t('Quantity')" sortable style="width: 100px">
                    <template #body="slotProps">
                        <Tag :value="slotProps.data.quantity" severity="info" rounded />
                    </template>
                </Column>
                <Column field="added_at" :header="$t('Added At')" sortable style="min-width: 160px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-600">{{ formatDateTime(slotProps.data.added_at) }}</span>
                    </template>
                </Column>
            </DataTable>
        </Panel>

        <!-- Orders Section -->
        <Panel toggleable collapsed>
            <template #header>
                <div class="flex items-center gap-3">
                    <i class="pi pi-file text-green-600"></i>
                    <span class="font-semibold">{{ $t('Orders with this Site') }}</span>
                    <Tag severity="secondary" rounded class="ml-2">{{ ordersWithSite.length }}</Tag>
                </div>
            </template>
            <DataTable
                :value="ordersWithSite"
                paginator
                :rows="10"
                dataKey="id"
                class="p-datatable-sm"
                stripedRows
                :rowHover="true"
            >
                <template #empty>
                    <div class="w-full flex flex-col items-center justify-center py-6 text-surface-500">
                        <i class="pi pi-file text-4xl mb-2 text-surface-300"></i>
                        <span class="text-sm">{{ $t('No orders found with this site') }}</span>
                    </div>
                </template>

                <Column field="order_number" :header="$t('Order')" sortable style="width: 120px">
                    <template #body="slotProps">
                        <a :href="slotProps.data.order_url" class="text-blue-600 hover:text-blue-700 font-medium">
                            #{{ slotProps.data.order_number }}
                        </a>
                    </template>
                </Column>
                <Column field="client_name" :header="$t('Client')" sortable style="min-width: 160px">
                    <template #body="slotProps">
                        <div class="flex items-center gap-2">
                            <span class="font-medium">{{ slotProps.data.client_name }}</span>
                            <Tag v-if="slotProps.data.client_deleted" value="Deleted" severity="danger" class="text-xs" rounded />
                        </div>
                    </template>
                </Column>
                <Column field="seller_name" :header="$t('Seller')" sortable style="min-width: 120px">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.seller_name" class="text-sm text-surface-700">{{ slotProps.data.seller_name }}</span>
                        <span v-else class="text-surface-400">-</span>
                    </template>
                </Column>
                <Column field="sale" :header="$t('Amount')" sortable style="width: 110px">
                    <template #body="slotProps">
                        <span class="font-semibold text-green-700">{{ formatPrice(slotProps.data.sale, slotProps.data.sale_coin) }}</span>
                    </template>
                </Column>
                <Column field="type_name" :header="$t('Type')" sortable style="width: 100px">
                    <template #body="slotProps">
                        <Tag :value="slotProps.data.type_name || 'Default'" severity="info" rounded />
                    </template>
                </Column>
                <Column field="status" :header="$t('Status')" sortable style="width: 140px">
                    <template #body="slotProps">
                        <Tag :value="getOrderStatusLabel(slotProps.data.status)" :severity="getOrderStatusSeverity(slotProps.data.status)" rounded />
                    </template>
                </Column>
                <Column field="created_at" :header="$t('Date')" sortable style="width: 100px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-600">{{ formatDate(slotProps.data.created_at) }}</span>
                    </template>
                </Column>
                <template #footer>
                    <div class="flex gap-4 justify-end py-1">
                        <div
                            class="flex items-center gap-2 cursor-help"
                            v-tooltip.top="$t('Sum of all orders except canceled')"
                        >
                            <i class="pi pi-clock text-orange-500 text-sm"></i>
                            <span class="text-sm text-surface-600">{{ $t('Total Orders') }}:</span>
                            <span class="font-bold text-orange-600">{{ formatWithSymbol(totalPendingSale, currentCurrency) }}</span>
                        </div>
                        <div
                            class="flex items-center gap-2 cursor-help"
                            v-tooltip.top="$t('Sum of orders with status Completed or Invoice')"
                        >
                            <i class="pi pi-check-circle text-green-600 text-sm"></i>
                            <span class="text-sm text-surface-600">{{ $t('Total Completed') }}:</span>
                            <span class="font-bold text-green-700">{{ formatWithSymbol(totalCompletedSale, currentCurrency) }}</span>
                        </div>
                    </div>
                </template>
            </DataTable>
        </Panel>

        <!-- Live Links Section -->
        <Panel toggleable collapsed>
            <template #header>
                <div class="flex items-center gap-3">
                    <i class="pi pi-link text-purple-600"></i>
                    <span class="font-semibold">{{ $t('Published Live Links') }}</span>
                    <Tag severity="secondary" rounded class="ml-2">{{ liveLinksWithSite.length }}</Tag>
                </div>
            </template>
            <DataTable
                :value="liveLinksWithSite"
                paginator
                :rows="10"
                dataKey="id"
                class="p-datatable-sm"
                stripedRows
                :rowHover="true"
            >
                <template #empty>
                    <div class="w-full flex flex-col items-center justify-center py-6 text-surface-500">
                        <i class="pi pi-link text-4xl mb-2 text-surface-300"></i>
                        <span class="text-sm">{{ $t('No live links published for this site') }}</span>
                    </div>
                </template>

                <Column field="order_number" :header="$t('Order')" sortable style="width: 120px">
                    <template #body="slotProps">
                        <a :href="slotProps.data.order_url" class="text-blue-600 hover:text-blue-700 font-medium">
                            #{{ slotProps.data.order_number }}
                        </a>
                    </template>
                </Column>
                <Column field="client_name" :header="$t('Client')" sortable style="min-width: 160px">
                    <template #body="slotProps">
                        <div class="flex items-center gap-2">
                            <span class="font-medium">{{ slotProps.data.client_name }}</span>
                            <Tag v-if="slotProps.data.client_deleted" value="Deleted" severity="danger" class="text-xs" rounded />
                        </div>
                    </template>
                </Column>
                <Column field="link" :header="$t('Live Link')" sortable style="min-width: 280px">
                    <template #body="slotProps">
                        <div v-if="slotProps.data.link" class="flex items-center gap-2">
                            <a :href="slotProps.data.link" target="_blank" class="text-blue-600 hover:text-blue-700 hover:underline truncate max-w-xs">
                                {{ slotProps.data.link }}
                            </a>
                            <button
                                @click="copyToClipboard(slotProps.data.link, slotProps.data.id)"
                                class="p-1 rounded hover:bg-surface-100 transition-colors"
                                :class="copiedLinkId === slotProps.data.id ? 'text-green-600' : 'text-surface-500 hover:text-blue-600'"
                            >
                                <i :class="copiedLinkId === slotProps.data.id ? 'pi pi-check' : 'pi pi-copy'" class="text-sm"></i>
                            </button>
                        </div>
                        <span v-else class="text-surface-400">-</span>
                    </template>
                </Column>
                <Column field="link_status" :header="$t('Status')" sortable style="width: 120px">
                    <template #body="slotProps">
                        <Tag
                            :value="slotProps.data.formatted_link_status || slotProps.data.link_status || '-'"
                            :severity="slotProps.data.link_status === 'PUBLISHED' ? 'success' : 'info'"
                            rounded
                        />
                    </template>
                </Column>
                <Column field="confirmed_at" :header="$t('Confirmed')" sortable style="min-width: 140px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-600">{{ formatDateTime(slotProps.data.confirmed_at) }}</span>
                    </template>
                </Column>
            </DataTable>
        </Panel>

        <!-- Offers History Section -->
        <Panel>
            <template #header>
                <div class="flex items-center gap-3">
                    <i class="pi pi-history text-orange-600"></i>
                    <span class="font-semibold">{{ $t('Offers History') }}</span>
                    <Tag severity="secondary" rounded class="ml-2">{{ filteredOfferHistory.length }}</Tag>
                </div>
            </template>

            <!-- Filters Bar -->
            <div class="flex flex-wrap items-center gap-4 mb-4 p-3 bg-surface-50 rounded-lg border border-surface-200">
                <div class="flex items-center gap-2">
                    <label class="text-sm font-medium text-surface-600">{{ $t('Niche') }}:</label>
                    <Select
                        v-model="selectedNiche"
                        :options="nicheOptions"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('All niches')"
                        class="w-40"
                        size="small"
                    />
                </div>

                <div class="flex items-center gap-2">
                    <label class="text-sm font-medium text-surface-600">{{ $t('Change Type') }}:</label>
                    <Select
                        v-model="selectedChangeType"
                        :options="changeTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('All changes')"
                        class="w-40"
                        size="small"
                    />
                </div>

                <div class="flex items-center gap-2 border-l border-surface-300 pl-4">
                    <Checkbox
                        v-model="showOnlyDisplayPriceChanges"
                        inputId="displayPriceFilter"
                        :binary="true"
                    />
                    <label for="displayPriceFilter" class="text-sm font-medium text-surface-700 cursor-pointer">
                        {{ $t('Only changes affecting displayed price') }}
                    </label>
                </div>

                <div v-if="selectedNiche || selectedChangeType || showOnlyDisplayPriceChanges" class="ml-auto">
                    <button
                        @click="selectedNiche = null; selectedChangeType = null; showOnlyDisplayPriceChanges = false"
                        class="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                        <i class="pi pi-filter-slash text-xs"></i>
                        {{ $t('Clear filters') }}
                    </button>
                </div>
            </div>

            <DataTable
                :value="filteredOfferHistory"
                paginator
                :rows="15"
                dataKey="id"
                class="p-datatable-sm"
                stripedRows
                :rowHover="true"
            >
                <template #empty>
                    <div class="w-full flex flex-col items-center justify-center py-6 text-surface-500">
                        <i class="pi pi-history text-4xl mb-2 text-surface-300"></i>
                        <span class="text-sm">{{ $t('No offer history found') }}</span>
                    </div>
                </template>

                <Column field="changed_at" :header="$t('Date')" sortable style="width: 140px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-600">{{ formatDateTime(slotProps.data.changed_at) }}</span>
                    </template>
                </Column>
                <Column field="action" :header="$t('Action')" sortable style="width: 160px">
                    <template #body="slotProps">
                        <div class="flex flex-col gap-1">
                            <div class="flex items-center gap-1">
                                <Tag
                                    :value="getActionLabel(slotProps.data.action)"
                                    :severity="getActionSeverity(slotProps.data.action)"
                                    rounded
                                />
                                <Tag
                                    v-if="isFormulaChangeItem(slotProps.data)"
                                    value="Formula"
                                    severity="secondary"
                                    rounded
                                    class="text-xs"
                                />
                                <Tag
                                    v-if="isReferencePriceItem(slotProps.data)"
                                    severity="warn"
                                    rounded
                                    class="text-xs"
                                >
                                    <i class="pi pi-star-fill text-xs mr-1"></i>
                                    {{ $t('Ref') }}
                                </Tag>
                            </div>
                        </div>
                    </template>
                </Column>
                <Column field="seller_name" :header="$t('Seller')" sortable style="min-width: 120px">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.seller_name" class="text-sm font-medium text-surface-700">{{ slotProps.data.seller_name }}</span>
                        <span v-else class="text-surface-400">-</span>
                    </template>
                </Column>
                <Column field="type_name" :header="$t('Niche')" sortable style="width: 100px">
                    <template #body="slotProps">
                        <Tag :value="slotProps.data.type_name || 'Default'" severity="info" rounded />
                    </template>
                </Column>
                <Column :header="$t('Cost')" style="min-width: 180px">
                    <template #body="slotProps">
                        <div v-if="slotProps.data.old_values?.cost !== undefined && slotProps.data.new_values?.cost !== undefined && slotProps.data.old_values?.cost !== slotProps.data.new_values?.cost" class="flex items-center gap-1 flex-wrap">
                            <span class="text-red-500 line-through text-sm">{{ formatPrice(slotProps.data.old_values?.cost, slotProps.data.old_values?.cost_coin || slotProps.data.cost_coin) }}</span>
                            <i class="pi pi-arrow-right text-surface-400 text-xs"></i>
                            <span class="text-green-600 font-semibold text-sm">{{ formatPrice(slotProps.data.new_values?.cost, slotProps.data.new_values?.cost_coin ?? slotProps.data.cost_coin) }}</span>
                        </div>
                        <span v-else class="font-medium text-surface-700 text-sm">{{ formatPrice(slotProps.data.cost, slotProps.data.cost_coin) }}</span>
                    </template>
                </Column>
                <Column :header="$t('Sale')" style="min-width: 180px">
                    <template #body="slotProps">
                        <div v-if="slotProps.data.old_values?.sale !== undefined && slotProps.data.new_values?.sale !== undefined && slotProps.data.old_values?.sale !== slotProps.data.new_values?.sale" class="flex items-center gap-1 flex-wrap">
                            <span class="text-red-500 line-through text-sm">{{ formatPrice(slotProps.data.old_values?.sale, slotProps.data.old_values?.sale_coin || slotProps.data.sale_coin) }}</span>
                            <i class="pi pi-arrow-right text-surface-400 text-xs"></i>
                            <span class="text-green-600 font-semibold text-sm">{{ formatPrice(slotProps.data.new_values?.sale, slotProps.data.new_values?.sale_coin ?? slotProps.data.sale_coin) }}</span>
                        </div>
                        <span v-else class="font-semibold text-green-700 text-sm">{{ formatPrice(slotProps.data.sale, slotProps.data.sale_coin) }}</span>
                    </template>
                </Column>
                <Column field="changed_by_name" :header="$t('Changed By')" sortable style="min-width: 120px">
                    <template #body="slotProps">
                        <span class="text-sm text-surface-700">{{ slotProps.data.changed_by_name }}</span>
                    </template>
                </Column>
            </DataTable>
        </Panel>
    </div>
</template>

<style scoped>
.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
